import React, { MouseEvent, useCallback, useState } from 'react';

import { Icon } from '@rbilabs/components-library';
import { useIntl } from 'react-intl';
import { useMatch, useNavigate } from 'react-router-dom';

import OrderCancelledModal from 'components/order-cancelled-modal';
import {
  DeliveryStatus,
  IUserOrderFragment,
  RbiOrderStatus,
  useGetUserOrdersQuery,
} from 'generated/rbi-graphql';
import useDialogModal from 'hooks/use-dialog-modal';
import {
  STATUS_MESSAGES,
  STATUS_PERCENTS,
} from 'pages/order-confirmation/delivery/delivery-progress';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { ServiceMode, useOrderContext } from 'state/order';
import { useStoreContext } from 'state/store';
import { routes } from 'utils/routing';

import theme from '../theme';
import {
  BottomServiceVariants,
  IBottomServiceModeDetail,
  IOrderInProgress,
  IOrderInProgressResult,
} from '../types';
import { deliveryCompleteTimeout, postOrderTimeout } from '../utils';

import { useCartButtonText } from './use-cart-button-text';

const DELIVERY_COMPLETED = [
  DeliveryStatus.ORDER_DROPPED_OFF,
  DeliveryStatus.ORDER_ABANDONED,
  DeliveryStatus.ORDER_CANCELLED,
];

const ORDER_PROGRESS_POLLING_DURATION_IN_MS = 10000;
const DELIVERY_COMPLETED_TIMEOUT_IN_MINUTES = 10;
export const PICKUP_UPDATED_TIMEOUT_IN_MINUTES = 10;
export const PRE_CURBSIDE_CONFIRM_ARRIVAL_TIMEOUT_IN_MINUTES = 60;

export const getDeliveryHeading = ({ deliveryStatus }: { deliveryStatus: DeliveryStatus }) => {
  const deliveryProgress = STATUS_PERCENTS[deliveryStatus];
  if (deliveryProgress <= STATUS_PERCENTS[DeliveryStatus.ORDER_CREATED]) {
    return 'orderPlaced';
  }
  if (deliveryProgress < STATUS_PERCENTS[DeliveryStatus.ORDER_PICKED_UP]) {
    return 'deliveryOrderBeingPrepared';
  }
  if (deliveryProgress < STATUS_PERCENTS[DeliveryStatus.ORDER_DROPPED_OFF]) {
    return 'deliveryDriverEnroute';
  }
  return 'deliveryDelivered';
};

export const isPollingForDelivery = (
  recentOrder: IUserOrderFragment | null | undefined,
  isDeliveryOrderCompleted: boolean
) => {
  if (
    recentOrder?.delivery &&
    !isDeliveryOrderCompleted &&
    !DELIVERY_COMPLETED.includes(recentOrder.delivery.status)
  ) {
    return true;
  }
  return false;
};

// Timeout time of 10 minutes post order placed:
// - ServiceMode.TAKEOUT
// - ServiceMode.DRIVE_THRU
// - ServiceMode.EAT_IN
// - ServiceMode.CURBSIDE
// - ServiceMode.DELIVERY
// Timeout time of 60 minutes until order placed, pre-confirm arrival:
// - ServiceMode.CURBSIDE
export const orderInProgress = ({
  recentOrder,
  curbsidePickupOrderId,
  curbsidePickupOrderTimePlaced,
  isDeliveryOrderCompleted,
}: IOrderInProgress): IOrderInProgressResult | null => {
  if (
    recentOrder &&
    [ServiceMode.CATERING_PICKUP, ServiceMode.CATERING_DELIVERY].includes(
      recentOrder?.cart.serviceMode
    )
  ) {
    return null;
  }

  const isPickupTimedOut = () =>
    recentOrder &&
    !recentOrder?.delivery &&
    postOrderTimeout(recentOrder?.updatedAt, PICKUP_UPDATED_TIMEOUT_IN_MINUTES);

  const isPreConfirmCurbsideTimedOut = () =>
    curbsidePickupOrderId &&
    postOrderTimeout(
      curbsidePickupOrderTimePlaced,
      PRE_CURBSIDE_CONFIRM_ARRIVAL_TIMEOUT_IN_MINUTES
    );

  // ServiceMode.DELIVERY - ordered
  if (recentOrder?.delivery && !isDeliveryOrderCompleted) {
    const deliveryStatus = recentOrder.delivery.status;
    const headingId = getDeliveryHeading({ deliveryStatus });
    const detailsId = STATUS_MESSAGES[deliveryStatus];
    const buttonContentTextId =
      deliveryStatus === DeliveryStatus.ORDER_DROPPED_OFF ? 'details' : 'track';
    return {
      headingId,
      detailsId,
      buttonContentTextId,
      buttonContentIcon: null,
      icon: <Icon icon="markerFilled" color="white" width="1.75rem" height="1.75rem" />,
      variant: BottomServiceVariants.IN_PROGRESS,
    };
  }
  // PICKUP - ordered
  if (recentOrder && isPickupTimedOut()) {
    return {
      headingId: 'orderPlaced',
      detailsId: 'yourOrderIsBeingPrepared',
      icon: <Icon icon="restaurant" color="white" width="1.75rem" height="1.75rem" />,
      buttonContentTextId: 'details',
      buttonContentIcon: null,
      variant: BottomServiceVariants.IN_PROGRESS,
    };
  }
  // ServiceMode.CURBSIDE - Before order is commited on confirming arrival , selecting "I'm here!"
  if (isPreConfirmCurbsideTimedOut()) {
    return {
      headingId: 'serviceModeDetailsCurbsideHeader',
      detailsId: 'headToRestaurant',
      icon: <Icon icon="restaurant" color="white" width="1.75rem" height="1.75rem" />,
      buttonContentTextId: 'confirmArrival',
      buttonContentIcon: null,
      variant: BottomServiceVariants.IN_PROGRESS,
    };
  }
  return null;
};

export const useBottomServiceModeDetails = (): IBottomServiceModeDetail => {
  const {
    serviceMode,
    deliveryAddress,
    canUserCheckout,
    curbsidePickupOrderId,
    curbsidePickupOrderTimePlaced,
  } = useOrderContext();
  const { store } = useStoreContext();
  const enableOrdering = useFlag(LaunchDarklyFlag.ENABLE_ORDERING);
  const { formatMessage } = useIntl();
  const { logEvent } = useMParticleContext();
  const { isAuthenticated } = useAuthContext();
  const isAuth = isAuthenticated();
  const [hasSeenCancelModal, setHasSeenCancelModal] = useState(false);
  const [CancellationModal, openCancellationModal] = useDialogModal({
    Component: OrderCancelledModal,
    onDismiss: () => setHasSeenCancelModal(true),
  });
  const { cartButtonText } = useCartButtonText();

  const isOnMenuPage = useMatch({
    path: routes.menu,
    end: false,
  });
  const navigate = useNavigate();
  const { data, startPolling, stopPolling, refetch } = useGetUserOrdersQuery({
    variables: {
      limit: 1,
      orderStatuses: [
        RbiOrderStatus.INSERT_SUCCESSFUL,
        RbiOrderStatus.UPDATE_SUCCESSFUL,
        RbiOrderStatus.REFUND_SUCCESSFUL,
      ],
    },
  });

  const handleCartClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      logEvent(CustomEventNames.BOTTOM_SERVICE_CART, EventTypes.Other, {
        'Service Mode': serviceMode,
      });
      store._id ? navigate(routes.cart) : navigate(routes.serviceMode);
    },
    [logEvent, navigate, serviceMode, store._id]
  );
  const handleServiceModeChange = useCallback(() => {
    logEvent(CustomEventNames.BOTTOM_SERVICE_MODE, EventTypes.Other, {
      'Service Mode': serviceMode,
    });
    navigate(routes.serviceMode);
  }, [logEvent, navigate, serviceMode]);
  const handleNavigateToOrderConfirmation = useCallback(
    (rbiOrderId: string) => () => {
      logEvent(CustomEventNames.BOTTOM_SERVICE_MODE, EventTypes.Other, {
        'Service Mode': serviceMode,
        'Order Id': rbiOrderId,
      });
      navigate(`${routes.orderConfirmation}/${rbiOrderId}`);
    },
    [logEvent, navigate, serviceMode]
  );
  // We don't want to block user from ordering when there is existing
  // order in progress. We will always shows default bottom service mode
  // on menu page
  const recentOrder = data?.userOrders?.orders?.[0];

  // This will change the display of BSM after the order has been placed
  // and delivery is being tracked, a waiting that pickup from curbside.
  // Any refunds will not block users from making a new order
  if (!isOnMenuPage && isAuth && recentOrder?.status !== RbiOrderStatus.REFUND_SUCCESSFUL) {
    // Completed delivery order is when status is completed and
    // exceeded 10 minutes after order is dropped off (in order for user to view the order)
    const isDeliveryOrderCompleted =
      !!recentOrder?.delivery &&
      DELIVERY_COMPLETED.includes(recentOrder.delivery.status) &&
      (hasSeenCancelModal ||
        deliveryCompleteTimeout(recentOrder?.updatedAt, DELIVERY_COMPLETED_TIMEOUT_IN_MINUTES));

    if (isPollingForDelivery(recentOrder, isDeliveryOrderCompleted)) {
      hasSeenCancelModal && setHasSeenCancelModal(false);
      startPolling(ORDER_PROGRESS_POLLING_DURATION_IN_MS);
    }

    const inProgressResult = orderInProgress({
      recentOrder,
      curbsidePickupOrderId,
      curbsidePickupOrderTimePlaced,
      isDeliveryOrderCompleted,
    });
    if (inProgressResult) {
      const handleNavArg: string = curbsidePickupOrderId || recentOrder?.rbiOrderId;
      const onButtonClick =
        recentOrder?.delivery?.status === DeliveryStatus.ORDER_CANCELLED
          ? openCancellationModal
          : handleNavigateToOrderConfirmation(handleNavArg);

      return {
        ...inProgressResult,
        heading: formatMessage({ id: inProgressResult.headingId }),
        details: formatMessage({ id: inProgressResult.detailsId }),
        buttonContentText: formatMessage({ id: inProgressResult.buttonContentTextId }),
        onButtonClick,
        onBottomServiceClick: onButtonClick,
        CancellationModal,
        refetchUserOrder: refetch,
      };
    }
  }

  const resultBase: IBottomServiceModeDetail = {
    heading: formatMessage({ id: 'forItemAvailability' }),
    details: formatMessage({ id: 'chooseALocation' }),
    icon: (
      <Icon
        icon="markerFilled"
        color={theme.iconContainer.color}
        width="1.75rem"
        height="1.75rem"
      />
    ),
    buttonContentText: cartButtonText,
    buttonContentIcon: <Icon icon="bag" color={theme.iconContainer.color} />,
    onButtonClick: handleCartClick,
    onBottomServiceClick: handleServiceModeChange,
    buttonDisabled: !canUserCheckout,
    variant: BottomServiceVariants.DEFAULT,
    CancellationModal,
    refetchUserOrder: refetch,
  };

  stopPolling();
  if (store._id) {
    if ((!store.hasMobileOrdering || !enableOrdering) && store.physicalAddress?.address1) {
      return {
        ...resultBase,
        heading: formatMessage({ id: 'yourSelectedStore' }),
        details: store.physicalAddress.address1,
        icon: (
          <Icon
            icon="restaurant"
            color={theme.iconContainer.color}
            width="1.75rem"
            height="1.75rem"
          />
        ),
        buttonContentText: '',
        buttonContentIcon: null,
      };
    }
    switch (serviceMode) {
      case ServiceMode.DELIVERY:
        if (deliveryAddress) {
          return {
            ...resultBase,
            heading: formatMessage({ id: 'delivery' }),
            details: deliveryAddress.addressLine1,
            icon: (
              <Icon
                icon={theme.delivery.icon}
                color={theme.iconContainer.color}
                width={theme.delivery.width}
                height={theme.delivery.height}
              />
            ),
          };
        }
        break;
      case ServiceMode.CATERING_DELIVERY:
        if (deliveryAddress) {
          return {
            ...resultBase,
            heading: formatMessage({ id: 'cateringDashDelivery' }),
            details: deliveryAddress.addressLine1,
            icon: (
              <Icon
                icon={theme.catering.delivery.icon}
                color={theme.iconContainer.color}
                width={theme.catering.delivery.width}
                height={theme.catering.delivery.height}
              />
            ),
          };
        }
        break;
      case ServiceMode.CATERING_PICKUP:
        if (store.physicalAddress?.address1) {
          return {
            ...resultBase,
            heading: formatMessage({ id: 'cateringDashPickUp' }),
            details: store.physicalAddress.address1,
            icon: (
              <Icon
                icon={theme.catering.pickup.icon}
                color={theme.iconContainer.color}
                width={theme.catering.pickup.width}
                height={theme.catering.pickup.height}
              />
            ),
          };
        }
        break;
      case ServiceMode.TAKEOUT:
      case ServiceMode.CURBSIDE:
      case ServiceMode.DRIVE_THRU:
      case ServiceMode.EAT_IN:
        if (store.physicalAddress?.address1) {
          return {
            ...resultBase,
            heading: formatMessage({ id: 'pickUp' }),
            details: store.physicalAddress.address1,
            icon: (
              <Icon
                icon={theme.pickup.icon}
                color={theme.iconContainer.color}
                width={theme.pickup.width}
                height={theme.pickup.height}
              />
            ),
          };
        }
        break;
      default:
    }
  }
  return {
    ...resultBase,
    buttonContentText: '0',
    buttonContentIcon: <Icon icon="bag" color="black" />,
    buttonDisabled: undefined,
    variant: BottomServiceVariants.NO_STORE,
  };
};
